<script setup lang="ts">
import { computed, toRef } from 'vue';
import { i18n, t, tArray } from '@/i18n';
import { Amplitude } from '@seede/amplitude';

const locale = toRef(i18n.options, 'locale');

const expandedFaqIndex = ref<number | null>(null);
const pagePath = computed(() => (import.meta.client ? window.location.href : ''));

const toggleFaq = (index: number) => {
  const item = faqItems.value[index];
  const willExpand = expandedFaqIndex.value !== index;
  Amplitude.event('cta_click', {
    cta_name: 'faq_toggle',
    section: 'faq',
    faq_index: index,
    faq_question: item?.question,
    faq_action: willExpand ? 'expand' : 'collapse',
    page_path: pagePath.value,
  });
  expandedFaqIndex.value = expandedFaqIndex.value === index ? null : index;
};

const trackFaqLinkClick = (url: string, text: string, index: number) => {
  Amplitude.event('nav_link_click', {
    link_text: text,
    link_target: url,
    section: 'faq',
    faq_index: index,
    page_path: pagePath.value,
  });
};

const faqItems = computed(() => {
  void locale.value;

  return [
    {
      question: t('landing_home.faq.1.question'),
      answerParagraph1: t('landing_home.faq.1.answer_p1'),
      answerParagraph2: t('landing_home.faq.1.answer_p2'),
      answerParagraph3: t('landing_home.faq.1.answer_p3'),
      products: tArray('landing_home.faq.1.product'),
    },
    {
      question: t('landing_home.faq.2.question'),
      answerParagraph1: t('landing_home.faq.2.answer_p1'),
      listItems: tArray('landing_home.faq.2.list'),
    },
    {
      question: t('landing_home.faq.3.question'),
      answer: t('landing_home.faq.3.answer'),
    },
    {
      question: t('landing_home.faq.4.question'),
      answerParagraph1: t('landing_home.faq.4.answer_p1'),
      listItems: tArray('landing_home.faq.4.list_a'),
      answerParagraph2: t('landing_home.faq.4.answer_p2'),
      listItems2: tArray('landing_home.faq.4.list_b'),
      answerParagraph3: t('landing_home.faq.4.answer_p3'),
    },
    {
      question: t('landing_home.faq.5.question'),
      answer: t('landing_home.faq.5.answer'),
    },
    {
      question: t('landing_home.faq.6.question'),
      answerParagraph1: t('landing_home.faq.6.answer_p1'),
      listItems: tArray('landing_home.faq.6.list'),
      answerParagraph2: t('landing_home.faq.6.answer_p2'),
    },
    {
      question: t('landing_home.faq.7.question'),
      answer: t('landing_home.faq.7.answer'),
    },
    {
      question: t('landing_home.faq.8.question'),
      answer: t('landing_home.faq.8.answer'),
    },
    {
      question: t('landing_home.faq.9.question'),
      answer: t('landing_home.faq.9.answer'),
    },
    {
      question: t('landing_home.faq.10.question'),
      answer: t('landing_home.faq.10.answer'),
    },
    {
      question: t('landing_home.faq.11.question'),
      answer: t('landing_home.faq.11.answer'),
    },
    {
      question: t('landing_home.faq.12.question'),
      answer: t('landing_home.faq.12.answer'),
    },
  ];
});

const faqTitle = computed(() => {
  void locale.value;
  return t('landing_home.faq.title');
});
</script>

<template>
  <section class="py-24 bg-white">
    <div class="container mx-auto px-4">
      <div class="max-w-[800px] mx-auto">
        <!-- FAQ Title -->
        <h2
          class="font-boldonse text-[clamp(1.5rem,3vw,2rem)] text-black/90 uppercase leading-[1.6] mb-12 text-center landing-title-section-md"
        >
          {{ faqTitle }}
        </h2>

        <!-- FAQ Items -->
        <div class="space-y-2">
          <div
            v-for="(item, index) in faqItems"
            :key="index"
            class="bg-black/[0.02] rounded-2xl px-5 transition-all duration-300"
          >
            <button
              type="button"
              @click="toggleFaq(index)"
              class="flex items-center justify-between w-full py-4 text-left font-normal transition-all text-black/90"
            >
              <h3 class="text-lg md:text-xl font-normal text-black/90 pr-4">{{ item.question }}</h3>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                class="h-4 w-4 shrink-0 text-black/40 transition-all duration-200"
              >
                <line
                  x1="8"
                  y1="2"
                  x2="8"
                  y2="14"
                  stroke="currentColor"
                  stroke-width="1"
                  :class="[expandedFaqIndex === index ? 'opacity-0' : '']"
                  style="transition: opacity 0.2s"
                />
                <line x1="2" y1="8" x2="14" y2="8" stroke="currentColor" stroke-width="1" />
              </svg>
            </button>
            <div
              v-show="expandedFaqIndex === index"
              class="pb-5 transition-all duration-200 ease-in-out"
            >
              <div v-if="item.answerParagraph1" class="space-y-3">
                <p class="text-base text-black/70 font-normal">
                  {{ item.answerParagraph1 }}
                  <span v-if="(item as any).link">
                    <a
                      :href="(item as any).link.url"
                      :target="(item as any).link.url.startsWith('http') ? '_blank' : '_self'"
                      :rel="(item as any).link.url.startsWith('http') ? 'noopener noreferrer' : ''"
                      class="underline inline-flex items-center ml-1"
                      @click="
                        trackFaqLinkClick((item as any).link.url, (item as any).link.text, index)
                      "
                    >
                      {{ (item as any).link.text }}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        class="w-4 h-4 ml-1"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </a>
                  </span>
                </p>
                <!-- List items (show after answerParagraph1, before answerParagraph2 if both exist) -->
                <ul
                  v-if="
                    (item as any).listItems &&
                    (item as any).listItems.length > 0 &&
                    (item as any).answerParagraph1 &&
                    (item as any).answerParagraph2
                  "
                  class="space-y-2.5 pl-0 mt-3"
                >
                  <li
                    v-for="(listItem, listIndex) in (item as any).listItems"
                    :key="listIndex"
                    class="text-base text-black/70 font-normal flex items-start"
                  >
                    <span class="text-black/40 mr-3 flex-shrink-0 mt-0.5">•</span>
                    <span>{{ listItem.replace('• ', '') }}</span>
                  </li>
                </ul>
                <!-- Regular paragraphs (always show if present) -->
                <p
                  v-if="
                    (item as any).answerParagraph2 &&
                    typeof (item as any).answerParagraph2 === 'string' &&
                    (item as any).answerParagraph2.trim() !== ''
                  "
                  class="text-base text-black/70 font-normal"
                >
                  {{ (item as any).answerParagraph2 }}
                </p>
                <!-- Second list items (show after answerParagraph2) -->
                <ul
                  v-if="(item as any).listItems2 && (item as any).listItems2.length > 0"
                  class="space-y-2.5 pl-0 mt-3"
                >
                  <li
                    v-for="(listItem, listIndex) in (item as any).listItems2"
                    :key="listIndex"
                    class="text-base text-black/70 font-normal flex items-start"
                  >
                    <span class="text-black/40 mr-3 flex-shrink-0 mt-0.5">•</span>
                    <span>{{ listItem.replace('• ', '') }}</span>
                  </li>
                </ul>
                <!-- List items (show after answerParagraph2 if no answerParagraph1 before it) -->
                <ul
                  v-if="
                    (item as any).listItems &&
                    (item as any).listItems.length > 0 &&
                    !(item as any).listItems2 &&
                    !(item as any).answerParagraph1
                  "
                  class="space-y-2.5 pl-0 mt-3"
                >
                  <li
                    v-for="(listItem, listIndex) in (item as any).listItems"
                    :key="listIndex"
                    class="text-base text-black/70 font-normal flex items-start"
                  >
                    <span class="text-black/40 mr-3 flex-shrink-0 mt-0.5">•</span>
                    <span>{{ listItem.replace('• ', '') }}</span>
                  </li>
                </ul>
                <!-- List items (show after answerParagraph1 if no answerParagraph2) -->
                <ul
                  v-if="
                    (item as any).listItems &&
                    (item as any).listItems.length > 0 &&
                    !(item as any).listItems2 &&
                    (item as any).answerParagraph1 &&
                    !(item as any).answerParagraph2
                  "
                  class="space-y-2.5 pl-0 mt-3"
                >
                  <li
                    v-for="(listItem, listIndex) in (item as any).listItems"
                    :key="listIndex"
                    class="text-base text-black/70 font-normal flex items-start"
                  >
                    <span class="text-black/40 mr-3 flex-shrink-0 mt-0.5">•</span>
                    <span>{{ listItem.replace('• ', '') }}</span>
                  </li>
                </ul>
                <p
                  v-if="
                    (item as any).answerParagraph3 &&
                    typeof (item as any).answerParagraph3 === 'string' &&
                    (item as any).answerParagraph3.trim() !== ''
                  "
                  class="text-base text-black/70 font-normal"
                >
                  {{ (item as any).answerParagraph3 }}
                  <span v-if="(item as any).products && (item as any).products.length > 0">
                    <template
                      v-for="(product, productIndex) in (item as any).products"
                      :key="productIndex"
                    >
                      <template v-if="productIndex === 0">{{ product }}</template>
                      <template v-else-if="productIndex === (item as any).products.length - 1"
                        >, and {{ product }}</template
                      >
                      <template v-else>, {{ product }}</template>
                    </template>
                    <span>.</span>
                  </span>
                </p>
                <p
                  v-if="(item as any).answerParagraph4"
                  class="text-base text-black/70 font-normal"
                >
                  {{ (item as any).answerParagraph4 }}
                </p>
                <p
                  v-if="(item as any).answerParagraph5"
                  class="text-base text-black/70 font-normal"
                >
                  {{ (item as any).answerParagraph5 }}
                </p>
                <p
                  v-if="(item as any).answerParagraph7"
                  class="text-base text-black/70 font-normal"
                >
                  {{ (item as any).answerParagraph7 }}
                </p>
              </div>
              <p v-else class="text-base text-black/70 font-normal">
                {{ item.answer }}
                <span v-if="(item as any).link">
                  <a
                    :href="(item as any).link.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="underline inline-flex items-center ml-1"
                    @click="
                      trackFaqLinkClick((item as any).link.url, (item as any).link.text, index)
                    "
                  >
                    {{ (item as any).link.text }}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      class="w-4 h-4 ml-1"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </a>
                </span>
                <br v-if="(item as any).answerAfterLink" />
                <span v-if="(item as any).answerAfterLink">{{
                  (item as any).answerAfterLink
                }}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
